import React from 'react';

import { sanitizeUrl } from '../../../services/urlSafety';
import styles from './FormattedText.module.css';
import { FormattedTextProps } from './FormattedText.types';

/**
 * Escapes unsafe HTML special characters in plain text to prevent injection.
 */
const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

/**
 * Safely parses inline markdown / formatting tokens (bold, italic, underline,
 * code, links, images) into HTML markup while protecting URLs and attributes.
 */
const parseInlineFormatting = (input: string): string => {
  if (!input) return '';

  const tokens: string[] = [];
  const pushToken = (html: string): string => {
    const token = `@@FORMATTOKEN${tokens.length}@@`;
    tokens.push(html);
    return token;
  };

  let str = input;

  // 1. Extract markdown images: ![alt](url)
  str = str.replace(/!\[(.*?)\]\((.*?)\)/g, (_, alt, src) => {
    const safeAlt = escapeHtml(alt);
    const safeSrc = escapeHtml(sanitizeUrl(src));
    return pushToken(
      `<div class="${styles.formattedImageWrapper}"><img src="${safeSrc}" alt="${safeAlt}" class="${styles.formattedImage}" />${
        safeAlt ? `<span class="${styles.imageCaption}">${safeAlt}</span>` : ''
      }</div>`
    );
  });

  // 2. Extract markdown links: [text](url)
  str = str.replace(/\[(.*?)\]\((.*?)\)/g, (_, text, url) => {
    const safeText = escapeHtml(text);
    const safeUrl = escapeHtml(sanitizeUrl(url));
    return pushToken(
      `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${safeText}</a>`
    );
  });

  // 3. Extract inline code: `code`
  str = str.replace(/`(.*?)`/g, (_, code) => {
    return pushToken(`<code>${escapeHtml(code)}</code>`);
  });

  // 4. Escape remaining raw HTML characters in text
  str = escapeHtml(str);

  // 5. Bold: **text** or __text__
  str = str.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  str = str.replace(/\b__(.*?)__\b/g, '<strong>$1</strong>');

  // 6. Italic: *text* or _text_ (strictly bounded to avoid breaking underscores in words/filenames)
  str = str.replace(/\*(.*?)\*/g, '<em>$1</em>');
  str = str.replace(/(^|\s)_([^_]+)_(?=\s|$)/g, '$1<em>$2</em>');

  // 7. Unescape safe allowed HTML formatting tags typed explicitly by author (e.g. <b>, <i>, <u>, <mark>)
  str = str
    .replace(/&lt;b&gt;(.*?)&lt;\/b&gt;/gi, '<strong>$1</strong>')
    .replace(/&lt;strong&gt;(.*?)&lt;\/strong&gt;/gi, '<strong>$1</strong>')
    .replace(/&lt;i&gt;(.*?)&lt;\/i&gt;/gi, '<em>$1</em>')
    .replace(/&lt;em&gt;(.*?)&lt;\/em&gt;/gi, '<em>$1</em>')
    .replace(/&lt;u&gt;(.*?)&lt;\/u&gt;/gi, '<u>$1</u>')
    .replace(/&lt;mark&gt;(.*?)&lt;\/mark&gt;/gi, '<mark>$1</mark>')
    .replace(
      /&lt;img src=&quot;(.*?)&quot;(?: alt=&quot;(.*?)&quot;)?\s*\/?&gt;/gi,
      (_, src, alt = '') =>
        pushToken(
          `<div class="${styles.formattedImageWrapper}"><img src="${sanitizeUrl(src)}" alt="${alt}" class="${styles.formattedImage}" />${
            alt ? `<span class="${styles.imageCaption}">${alt}</span>` : ''
          }</div>`
        )
    );

  // 8. Restore protected tokens (images, links, code). A global regex replace guards
  // against plain text that happens to contain a literal "@@FORMATTOKENn@@" marker
  // leaving a genuine token unreplaced (String#replace with a string pattern only
  // substitutes the first match).
  tokens.forEach((tokenHtml, idx) => {
    str = str.replace(new RegExp(`@@FORMATTOKEN${idx}@@`, 'g'), () => tokenHtml);
  });

  return str;
};

const parseTableRow = (rowLine: string): string[] => {
  let line = rowLine.trim();
  if (line.startsWith('|')) line = line.substring(1);
  if (line.endsWith('|')) line = line.substring(0, line.length - 1);
  return line.split('|').map((cell) => cell.trim());
};

const isTableDivider = (line: string): boolean => {
  const trimmed = line.trim();
  return (
    trimmed.includes('|') &&
    trimmed.includes('-') &&
    /^\|?\s*:?-+:?\s*(\|?\s*:?-+:?\s*)*\|?$/.test(trimmed)
  );
};

/**
 * Checks whether there is non-empty content under the heading at startIndex
 * before encountering another heading or reaching the end of lines.
 */
const hasContentUnderHeading = (startIndex: number, lines: string[]): boolean => {
  for (let j = startIndex + 1; j < lines.length; j++) {
    const trimmedLine = lines[j].trim();
    if (!trimmedLine) continue;
    if (/^(#{1,3})\s+(.*)$/.test(trimmedLine)) {
      return false;
    }
    return true;
  }
  return false;
};

/**
 * Parses multi-line block markdown structures (headings, lists, blockquotes, code blocks, tables)
 * into a structured tree of HTML strings.
 */
const renderBlocks = (text: string, collapsibleHeadings: boolean = true): string => {
  if (!text || !text.trim()) return '';

  const lines = text.split('\n');
  const result: string[] = [];
  let inList: 'ul' | 'ol' | null = null;
  let inCodeBlock = false;
  let codeBuffer: string[] = [];
  let openDetails = false;

  const closeDetailsIfOpen = () => {
    if (openDetails) {
      result.push('</div></details>');
      openDetails = false;
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Code block toggle (```)
    if (trimmed.startsWith('```')) {
      if (inCodeBlock) {
        result.push(`<pre><code>${escapeHtml(codeBuffer.join('\n'))}</code></pre>`);
        codeBuffer = [];
        inCodeBlock = false;
      } else {
        if (inList) {
          result.push(`</${inList}>`);
          inList = null;
        }
        inCodeBlock = true;
      }
      continue;
    }

    if (inCodeBlock) {
      codeBuffer.push(line);
      continue;
    }

    // Markdown Table Detection (supports tight and loose table formatting with blank lines)
    if (trimmed.includes('|')) {
      let dividerIdx = -1;
      for (let j = i + 1; j < Math.min(i + 4, lines.length); j++) {
        const lineJ = lines[j].trim();
        if (lineJ.length === 0) continue;
        if (isTableDivider(lineJ)) {
          dividerIdx = j;
          break;
        }
        break;
      }

      if (dividerIdx !== -1) {
        if (inList) {
          result.push(`</${inList}>`);
          inList = null;
        }

        const headerCells = parseTableRow(trimmed);
        const bodyRows: string[][] = [];
        i = dividerIdx + 1; // move past divider line

        while (i < lines.length) {
          const lineI = lines[i].trim();
          if (!lineI) {
            // Check if upcoming line within 3 lines contains '|'
            let nextHasPipe = false;
            for (let k = i + 1; k < Math.min(i + 3, lines.length); k++) {
              if (lines[k].trim()) {
                if (lines[k].trim().includes('|')) nextHasPipe = true;
                break;
              }
            }
            if (nextHasPipe) {
              i++;
              continue;
            } else {
              break;
            }
          }

          if (lineI.includes('|')) {
            bodyRows.push(parseTableRow(lineI));
            i++;
          } else {
            break;
          }
        }
        i--; // adjust loop index after table ends

        const headHtml = `<thead><tr>${headerCells
          .map((h) => `<th>${parseInlineFormatting(h)}</th>`)
          .join('')}</tr></thead>`;
        const bodyHtml = `<tbody>${bodyRows
          .map(
            (row) =>
              `<tr>${row.map((cell) => `<td>${parseInlineFormatting(cell)}</td>`).join('')}</tr>`
          )
          .join('')}</tbody>`;

        result.push(
          `<div class="${styles.tableWrapper}"><table class="${styles.formattedTable}">${headHtml}${bodyHtml}</table></div>`
        );
        continue;
      }
    }

    // Headings
    const headingMatch = trimmed.match(/^(#{1,3})\s+(.*)$/);
    if (headingMatch) {
      if (inList) {
        result.push(`</${inList}>`);
        inList = null;
      }

      closeDetailsIfOpen();

      const level = headingMatch[1].length;
      const headingText = parseInlineFormatting(headingMatch[2]);
      const tag = `h${level}`;

      const hasContent = hasContentUnderHeading(i, lines);

      if (collapsibleHeadings && hasContent) {
        result.push(
          `<details open class="${styles.collapsibleHeading}"><summary class="${styles.headingSummary}"><${tag} class="${styles.collapsibleHeadingTitle}">${headingText}</${tag}><svg class="${styles.headingChevron}" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/></svg></summary><div class="${styles.collapsibleHeadingContent}">`
        );
        openDetails = true;
      } else {
        result.push(`<${tag}>${headingText}</${tag}>`);
      }
      continue;
    }

    // Blockquotes
    if (trimmed.startsWith('> ')) {
      if (inList) {
        result.push(`</${inList}>`);
        inList = null;
      }
      result.push(`<blockquote>${parseInlineFormatting(trimmed.slice(2))}</blockquote>`);
      continue;
    }

    // Unordered List Items (- or *)
    const ulMatch = trimmed.match(/^[-*]\s+(.*)$/);
    if (ulMatch) {
      if (inList !== 'ul') {
        if (inList) result.push(`</${inList}>`);
        result.push('<ul>');
        inList = 'ul';
      }
      result.push(`<li>${parseInlineFormatting(ulMatch[1])}</li>`);
      continue;
    }

    // Ordered List Items (1., 2.)
    const olMatch = trimmed.match(/^\d+\.\s+(.*)$/);
    if (olMatch) {
      if (inList !== 'ol') {
        if (inList) result.push(`</${inList}>`);
        result.push('<ol>');
        inList = 'ol';
      }
      result.push(`<li>${parseInlineFormatting(olMatch[1])}</li>`);
      continue;
    }

    // Close any active list on non-list item line
    if (inList) {
      result.push(`</${inList}>`);
      inList = null;
    }

    // Empty line -> paragraph break spacer
    if (!trimmed) {
      continue;
    }

    // Standard paragraph or block content
    const parsedLine = parseInlineFormatting(line);
    if (parsedLine.startsWith('<div class="')) {
      result.push(parsedLine);
    } else {
      result.push(`<p>${parsedLine}</p>`);
    }
  }

  if (inList) {
    result.push(`</${inList}>`);
  }
  if (inCodeBlock && codeBuffer.length > 0) {
    result.push(`<pre><code>${escapeHtml(codeBuffer.join('\n'))}</code></pre>`);
  }
  closeDetailsIfOpen();

  return result.join('');
};

export const FormattedText: React.FC<FormattedTextProps> = ({
  text = '',
  className = '',
  collapsibleHeadings = true,
}) => {
  const htmlContent = renderBlocks(text, collapsibleHeadings);

  return (
    <div
      className={`${styles.formattedContainer} ${className}`}
      dangerouslySetInnerHTML={{ __html: htmlContent }}
      data-testid="formatted-text"
    />
  );
};
